import { QIcon_book, QIcon_chevron_left, QIcon_exclamation_filled } from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import { useEffect, useState } from 'react'
import apis from '../../../modules/apis'
import { axiosGet } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useWebviewNavigation from '../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_lesson, n_upcoming_lessons, s_lesson_style } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { useLessonsStack } from '../../../pages/[lang]/webview/lessons'
import LoadingSpinner from '../../common/LoadingSpinner'
import CreditBadge from '../../common/buttons/CreditBadge'
import TutorAvatar from '../../new-portal/lessons/TutorAvatar'
import WebviewStackContainer from '../common/WebviewStackContainer'

interface ILessonEnterBefore {
  params: {
    lessonId: string
  }
}

const EnterBar = ({ pop }) => {
  const { t } = useTranslation()

  return (
    <div className="flex w-screen flex-row items-center">
      <div className="ml-[20px] mr-[105px] " onClick={pop}>
        <QIcon_chevron_left className="h-24 w-24 text-black" />
      </div>
      <div className="flex flex-row items-center space-x-8">
        <span className="text-[16px] font-medium text-black">{t(n_lesson.popup_class_enter.title)}</span>
      </div>
    </div>
  )
}

const LessonEnterBeforeActivity: ActivityComponentType = ({ params }: ILessonEnterBefore) => {
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const { popBackToRingleApp } = useWebviewNavigation()
  const router = useAppRouter({ stackPush: push })
  const [settingInfo, setSettingInfo] = useState(null)
  const [item, setLessonData] = useState(null)
  const [upcomingData, setUpcomingData] = useState(null)
  const { currentUser } = useCurrentUser()
  const { t } = useTranslation()

  const fetchData = async () => {
    const response = await axiosGet(apis.lessons.setting(params.lessonId))

    if (response) {
      if (response.response) {
        setSettingInfo(response.response)
      }
    }
  }

  const fetchUpcomingData = async () => {
    const response = await axiosGet(apis.lessons.upcoming(params.lessonId))

    if (response) {
      setUpcomingData(response.data)
    }
  }

  useEffect(() => {
    fetchData()
    fetchUpcomingData()
  }, [])

  return (
    <WebviewStackContainer title={<EnterBar pop={popBackToRingleApp} />}>
      {settingInfo && upcomingData ? (
        <div className="relative flex overflow-y-auto px-[16px]">
          <div>
            <div className="border-1 mt-[8px] flex flex-col rounded-[10px] border-gray-200 bg-white p-[16px]">
              <div className="flex flex-col items-start justify-center">
                <div className="">
                  <CreditBadge
                    duration={upcomingData.lesson.duration}
                    style={{
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      color: '#140F33',
                      backgroundColor: 'rgb(228, 231, 244)',
                      fontWeight: 'bold',
                    }}
                  />
                </div>
                <div className="flex flex-row">
                  <span className="Title2-18Bd">{upcomingData?.formatted_start_date}</span>
                </div>
                <div className="flex flex-row items-start justify-center space-x-[8px]">
                  <span className="text-[20px] font-bold text-black">{upcomingData?.formatted_start_time}</span>
                  <span className="text-[14px] font-medium text-gray-500">{currentUser?.timezone}</span>
                </div>
              </div>
              <div className="mt-[8px]">
                <TutorAvatar tutor={upcomingData.tutor} />
              </div>
              <div className="mt-[24px]">
                <span className="Body2-14Bd flex text-gray-700">
                  <QIcon_book width="20px" height="20px" />
                  <span className="ml-[4px] font-bold">
                    {upcomingData.material_id.id
                      ? upcomingData?.course?.title
                        ? upcomingData?.course?.title
                        : upcomingData?.course?.subtitle1
                      : t(n_lesson.msg_material_auto3)}
                  </span>
                </span>
              </div>
            </div>
            <div className="mt-[32px]">
              <div className="flex flex-col gap-y-[16px]">
                <div className="flex items-start">
                  <div className="Body1-16Md min-w-[60px] text-gray-500">{t(n_lesson.popup_class.record)}</div>
                  <div className="Body1-16Md ml-[16px]">
                    {settingInfo.allow_record ? t(s_lesson_style.record.yes) : t(s_lesson_style.record.no)}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="Body1-16Md min-w-[60px] text-gray-500">{t(n_lesson.popup_class.analysis)}</div>
                  <div className="Body1-16Md ml-[16px]">
                    {settingInfo.allow_caf
                      ? t(n_upcoming_lessons.lesson_style.analysis_yes)
                      : t(n_upcoming_lessons.lesson_style.analysis_no)}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="Body1-16Md min-w-[60px] text-gray-500">{t(n_lesson.popup_class.intro)}</div>
                  <div className="Body1-16Md ml-[16px]">
                    {settingInfo.intro.skip_intro_user ? t(s_lesson_style.intro.skip) : t(s_lesson_style.intro.do)}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="Body1-16Md min-w-[60px] text-gray-500">{t(n_lesson.popup_class.mode)}</div>
                  <div className="Body1-16Md ml-[16px]">
                    {settingInfo.lesson_mode.mode == 0
                      ? t(s_lesson_style.lesson_mode.correction)
                      : t(s_lesson_style.lesson_mode.discussion)}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="Body1-16Md min-w-[60px] text-gray-500">
                    {t(n_lesson.popup_class.intensive_focus_title)}
                  </div>
                  <div className="Body1-16Md ml-[16px]">
                    {settingInfo.lesson_mode.mode == 0
                      ? settingInfo.lesson_mode.option == 0
                        ? t(s_lesson_style.mode_detail.correction.instant_short)
                        : t(s_lesson_style.mode_detail.correction.intermittent_short)
                      : settingInfo.lesson_mode.option == 0
                      ? t(s_lesson_style.mode_detail.discussion.mode55_short)
                      : t(s_lesson_style.mode_detail.discussion.mode82_short)}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="Body1-16Md min-w-[60px] text-gray-500">
                    {t(n_lesson.popup_class.additional_request)}
                  </div>
                  <div className="Body1-16Md ml-[16px] flex flex flex-wrap gap-x-[4px]">
                    {settingInfo.intensive_area.filter((item, i) => item.selected).length > 0
                      ? settingInfo.intensive_area
                          .filter((item, i) => item.selected)
                          .map((item, i) => (
                            <div
                              key={i}
                              className="Body2-14Bd break-words bg-gray-100 py-[4px] px-[10px] text-gray-600"
                            >
                              {item.title}
                            </div>
                          ))
                      : '-'}
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="Body1-16Md min-w-[60px] text-gray-500">{t(n_lesson.popup_class.other_request)}</div>
                  <div className="Body2-14Md ml-[16px] flex flex-wrap">
                    {settingInfo.other_request ? settingInfo.other_request : '-'}
                  </div>
                </div>
              </div>
              <div className="mt-[32px] flex items-center rounded-[6px] bg-[#FFF3EF] p-[12px]">
                <QIcon_exclamation_filled color="#FC997D" />
                <div className="Body2-14Md ml-[4px]">{t(n_lesson.popup_class.enter_info)}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-[20px]">
          <LoadingSpinner show lg primary />
        </div>
      )}
    </WebviewStackContainer>
  )
}

export default LessonEnterBeforeActivity
